import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react"
import { LexicalText, STARTING_NODES } from "@/schema"
import { CHECK_LIST, HEADING } from "@lexical/markdown"
import { InitialConfigType, LexicalComposer } from "@lexical/react/LexicalComposer"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary"
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin"
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin"
import { ListPlugin } from "@lexical/react/LexicalListPlugin"
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin"
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin"
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin"
import { TablePlugin } from "@lexical/react/LexicalTablePlugin"
import * as Sentry from "@sentry/nextjs"
import { cn } from "@ui/utils"
import {
  $createParagraphNode,
  $getRoot,
  $parseSerializedNode,
  COMMAND_PRIORITY_LOW,
  KEY_ENTER_COMMAND,
  LexicalEditor,
  SerializedEditorState,
} from "lexical"

import { useFetchEmailSnippets } from "@/hooks/api/email-snippets"
import { LEXICAL_NODES, MARKDOWN_TRANSFORMERS } from "@/components/lexical/config"
import LexicalContentEditable from "@/components/lexical/core/content-editable"
import baseStyles from "@/components/lexical/editors/base.module.css"
import { PlaceholderData, registerPlaceholders } from "@/components/lexical/placeholders"
import EnsureEditablePlugin from "@/components/lexical/plugins/ensure-editable"
import FloatingLinkEditorPlugin, { validateLinkUrl } from "@/components/lexical/plugins/floating-link-editor"
import FloatingLinkPreviewPlugin from "@/components/lexical/plugins/floating-link-preview"
import FloatingTextFormatToolbarPlugin from "@/components/lexical/plugins/floating-text-format-toolbar"
import InlineImagePlugin from "@/components/lexical/plugins/inline-image"
import ListMaxIndentLevelPlugin from "@/components/lexical/plugins/list-max-indent-level"
import OnContentChangePlugin from "@/components/lexical/plugins/on-content-change"
import PasteNormalizePlugin from "@/components/lexical/plugins/paste-normalize"
import PlaceholderPlugin from "@/components/lexical/plugins/placeholder"
import RawHtmlPlugin from "@/components/lexical/plugins/raw-html"
import SlashCommandPlugin from "@/components/lexical/plugins/slash-command"
import { htmlToNodes, toHTML } from "@/components/lexical/serializer"

import { EmailEditorProvider, getEmailEditorProfile } from "./provider-styles"
import styles from "./styles.module.css"

export interface EditorRefHandle {
  focus: () => void
  blur: () => void
  updateContent: (content: LexicalText) => void
  getContent: () => LexicalText | null
  getEditor: () => LexicalEditor | null
  hasFaulted: () => boolean
}

interface EmailEditorProps {
  enableSnippets?: boolean
  onChange?: (lexicalText: LexicalText) => void
  placeholder?: string
  initialContent?: LexicalText
  styleOverrides?: React.CSSProperties | Record<`--${string}`, string>
  enablePlaceholders?: boolean
  placeholders?: PlaceholderData[]
  plugins?: React.ReactNode
  editable?: boolean
  enableImages?: boolean
  caretBottomOffset?: number
  provider?: EmailEditorProvider
}

/** Plugin that captures the LexicalEditor instance so we can call .focus() */
function CaptureEditorPlugin({ setEditor }: { setEditor: (editor: LexicalEditor) => void }) {
  const [lexicalEditor] = useLexicalComposerContext()
  React.useEffect(() => {
    setEditor(lexicalEditor)
  }, [lexicalEditor, setEditor])
  return null
}

function findScrollParent(element: HTMLElement) {
  const radixViewport = element.closest("[data-radix-scroll-area-viewport]")
  if (radixViewport instanceof HTMLElement) return radixViewport

  let parent = element.parentElement
  while (parent) {
    const style = window.getComputedStyle(parent)
    const canScroll = /(auto|scroll)/.test(style.overflowY) && parent.scrollHeight > parent.clientHeight
    if (canScroll) return parent
    parent = parent.parentElement
  }

  return null
}

function getCaretRect() {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) return null

  const range = selection.getRangeAt(0).cloneRange()
  range.collapse(false)

  const rect = range.getBoundingClientRect()
  if (rect.height > 0 || rect.width > 0) return rect

  const container = range.endContainer
  const offset = range.endOffset

  if (container instanceof Text) {
    const textRange = document.createRange()

    if (offset > 0) {
      textRange.setStart(container, offset - 1)
      textRange.setEnd(container, offset)
      const previousCharacterRect = textRange.getBoundingClientRect()
      if (previousCharacterRect.height > 0 || previousCharacterRect.width > 0) {
        return new DOMRect(previousCharacterRect.right, previousCharacterRect.top, 0, previousCharacterRect.height)
      }
    }

    if (offset < container.length) {
      textRange.setStart(container, offset)
      textRange.setEnd(container, offset + 1)
      const nextCharacterRect = textRange.getBoundingClientRect()
      if (nextCharacterRect.height > 0 || nextCharacterRect.width > 0) {
        return new DOMRect(nextCharacterRect.left, nextCharacterRect.top, 0, nextCharacterRect.height)
      }
    }
  }

  const containerElement = container instanceof HTMLElement ? container : container.parentElement
  const containerRect = containerElement?.getBoundingClientRect()
  return containerRect && (containerRect.height > 0 || containerRect.width > 0) ? containerRect : null
}

function KeepCaretAboveBottomOffsetPlugin({ bottomOffset }: { bottomOffset: number }) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    let animationFrame: number | null = null

    const keepCaretVisible = () => {
      if (animationFrame !== null) cancelAnimationFrame(animationFrame)

      animationFrame = requestAnimationFrame(() => {
        const rootElement = editor.getRootElement()
        if (!rootElement) return

        const selection = window.getSelection()
        if (!selection?.anchorNode || !rootElement.contains(selection.anchorNode)) return

        const scrollParent = findScrollParent(rootElement)
        if (!scrollParent) return

        const caretRect = getCaretRect()
        if (!caretRect) return

        const viewportRect = scrollParent.getBoundingClientRect()
        const bottomLimit = viewportRect.bottom - bottomOffset

        if (caretRect.bottom > bottomLimit) {
          scrollParent.scrollTop += caretRect.bottom - bottomLimit + 8
        }
      })
    }

    const unregisterTextContentListener = editor.registerTextContentListener(keepCaretVisible)
    const unregisterEnterListener = editor.registerCommand(
      KEY_ENTER_COMMAND,
      () => {
        keepCaretVisible()
        return false
      },
      COMMAND_PRIORITY_LOW
    )

    return () => {
      if (animationFrame !== null) cancelAnimationFrame(animationFrame)
      unregisterTextContentListener()
      unregisterEnterListener()
    }
  }, [bottomOffset, editor])

  return null
}

function EmailEditorComponent(props: EmailEditorProps, forwardedRef: React.Ref<EditorRefHandle>) {
  const {
    onChange,
    placeholder = "Tip: hit / for styles, snippets, and variables",
    initialContent,
    styleOverrides,
    enablePlaceholders = false,
    placeholders,
    plugins,
    editable = true,
    enableImages = true,
    enableSnippets = true,
    caretBottomOffset,
    provider,
  } = props
  const { data: snippetData } = useFetchEmailSnippets({ enabled: enableSnippets && editable })
  const providerProfile = getEmailEditorProfile(provider)

  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null)
  const [lexicalEditor, setLexicalEditor] = useState<LexicalEditor | null>(null)
  const [editorContent, setEditorContent] = useState<SerializedEditorState | undefined>()
  const hasConvertedRef = useRef(false)
  const skipFirstOnChangeRef = useRef(true)
  const suppressNextOnChangeRef = useRef(false)
  const lastStoreJSONRef = useRef<string | null>(null)
  const hasFaultedRef = useRef(false)

  if (placeholders && placeholders.length) {
    registerPlaceholders(placeholders)
  }

  const emptyContent = () => {
    const root = $getRoot()
    root.clear()
    root.append($createParagraphNode())
  }

  useEffect(() => {
    if (!initialContent || (!initialContent.nodes && !initialContent.html)) {
      setEditorContent(STARTING_NODES)
      return
    }
    if (initialContent.nodes) {
      setEditorContent(initialContent.nodes)
    } else if (initialContent.html && !hasConvertedRef.current) {
      hasConvertedRef.current = true
      const convertedNodes = htmlToNodes(initialContent.html)
      if (convertedNodes) setEditorContent(convertedNodes)
    }
  }, [initialContent])

  // Apply a serialized state to the mounted editor and suppress the echo OnChange
  // that follows. We rebuild the root inside a single editor.update() rather than
  // calling setEditorState() on the live editor: setEditorState swaps the state
  // object out from under Lexical's MutationObserver, which then desyncs from the
  // live DOM — typed text never commits to the model and the next Enter wipes the
  // edited paragraph (HELP-1190). Rebuilding through update() keeps the observer
  // bound to the live node tree.
  //
  // A malformed or empty serialized state (e.g. an html-only body that doesn't
  // round-trip cleanly through the html->nodes import) falls back to an empty
  // paragraph instead of crashing the editor.
  const applySerialized = useCallback(
    (serialized: SerializedEditorState) => {
      if (!lexicalEditor) return

      let isValid = false
      try {
        isValid = !lexicalEditor.parseEditorState(serialized).isEmpty()
      } catch {
        isValid = false
      }

      suppressNextOnChangeRef.current = true
      lexicalEditor.update(() => {
        const root = $getRoot()
        root.clear()
        const children = isValid ? (serialized?.root?.children ?? []) : []
        if (children.length > 0) {
          children.forEach((child) => root.append($parseSerializedNode(child)))
        } else {
          root.append($createParagraphNode())
        }
      })
    },
    [lexicalEditor]
  )

  // Hydrate editor after mount or when the store pushes in content/signature.
  useEffect(() => {
    if (!lexicalEditor || !editorContent) return
    const incomingJSON = JSON.stringify(editorContent)
    // Only re-hydrate on genuine external pushes (template/signature). Skip the
    // echo of the editor's own onChange — handleOnSave already recorded it in
    // lastStoreJSONRef, and re-applying would reset the caret and can fight the
    // onChange loop.
    if (incomingJSON === lastStoreJSONRef.current) return
    const currentJSON = JSON.stringify(lexicalEditor.getEditorState().toJSON())
    if (incomingJSON !== currentJSON) {
      applySerialized(editorContent)
    }
    lastStoreJSONRef.current = incomingJSON
  }, [editorContent, lexicalEditor, applySerialized])

  const handleOnSave = (lexicalText: LexicalText) => {
    // 1) Skip the first OnChange (mount)
    if (skipFirstOnChangeRef.current) {
      skipFirstOnChangeRef.current = false
      return
    }
    // 2) Skip the echo right after any programmatic setEditorState
    if (suppressNextOnChangeRef.current) {
      suppressNextOnChangeRef.current = false
      return
    }
    if (lexicalEditor) {
      lastStoreJSONRef.current = JSON.stringify(lexicalEditor.getEditorState().toJSON())
    }
    onChange?.(lexicalText)
  }

  const handleEditorFault = useCallback((error: Error) => {
    hasFaultedRef.current = true
    Sentry.captureException(error)
    console.error(error)
  }, [])

  useImperativeHandle(forwardedRef, () => ({
    focus: () => {
      if (lexicalEditor) {
        lexicalEditor.focus()
      }
    },
    blur: () => {
      if (lexicalEditor) {
        lexicalEditor.blur()
      }
    },
    updateContent: (content: LexicalText) => {
      if (!lexicalEditor) return
      if (content.nodes) {
        applySerialized(content.nodes)
      } else if (content.html) {
        const convertedNodes = htmlToNodes(content.html)
        if (convertedNodes) applySerialized(convertedNodes)
      }
    },
    getContent: () => {
      if (!lexicalEditor) return null

      try {
        const editorState = lexicalEditor.getEditorState()
        let html = ""

        editorState.read(() => {
          html = toHTML(lexicalEditor)
        })

        return {
          nodes: editorState.toJSON(),
          html,
          preferred_format: "html",
        }
      } catch (error) {
        const editorError = error instanceof Error ? error : new Error("Failed to serialize email editor content")
        handleEditorFault(editorError)
        throw editorError
      }
    },
    getEditor: () => lexicalEditor,
    hasFaulted: () => hasFaultedRef.current,
  }))

  if (!editorContent) {
    // Only return empty if we have HTML that hasn't been converted yet
    if (initialContent && initialContent.html && !hasConvertedRef.current) {
      return <></>
    }

    // Otherwise, proceed with rendering (will use emptyContent fallback)
  }

  const initialConfig: InitialConfigType = {
    namespace: "EmailEditor",
    onError: handleEditorFault,
    theme: {
      text: {
        underline: baseStyles.underline,
        strikethrough: baseStyles.strikethrough,
        underlineStrikethrough: baseStyles.underlineStrikethrough,
      },
      link: baseStyles.link,
      inlineImage: baseStyles.inlineImage,
      table: cn(baseStyles.table, styles.table),
      tableScrollableWrapper: baseStyles.tableScrollableWrapper,
      tableCell: baseStyles.tableCell,
      tableCellHeader: baseStyles.tableCellHeader,
      tableCellSelected: baseStyles.tableCellSelected,
      tableSelected: baseStyles.tableSelected,
      tableCellResizer: baseStyles.tableCellResizer,
      tableSelection: baseStyles.tableSelection,
      list: {
        nested: {
          listitem: baseStyles.nestedListItem,
        },
      },
    },
    nodes: [...LEXICAL_NODES],
    editorState: editorContent ? JSON.stringify(editorContent) : emptyContent,
    editable,
  }

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem) {
      setFloatingAnchorElem(_floatingAnchorElem)
    }
  }

  const contentEditableElem = (
    <div className={baseStyles.editorContainer} ref={onRef}>
      <LexicalContentEditable
        placeholder={placeholder}
        proseClasses="prose-email dark:prose-invert"
        contentStyle={providerProfile.contentStyle}
      />
    </div>
  )

  const transformers = MARKDOWN_TRANSFORMERS.filter((t) => t !== HEADING && t !== CHECK_LIST)

  return (
    <div
      className={cn(baseStyles.base, styles.variables, "h-auto")}
      data-email-provider={providerProfile.provider}
      style={
        {
          "--lexical-editor-font-size": providerProfile.fontSize,
          "--email-editor-text-color": providerProfile.textColor,
          ...styleOverrides,
        } as React.CSSProperties & Record<`--${string}`, string>
      }
    >
      <LexicalComposer initialConfig={initialConfig}>
        <CaptureEditorPlugin setEditor={setLexicalEditor} />
        <HistoryPlugin />
        <EnsureEditablePlugin />
        <PasteNormalizePlugin />

        <RichTextPlugin ErrorBoundary={LexicalErrorBoundary} contentEditable={contentEditableElem} />

        {onChange && <OnContentChangePlugin onSave={handleOnSave} preferredFormat="html" />}

        <ListPlugin />
        <TablePlugin hasTabHandler hasHorizontalScroll={true} />
        <ListMaxIndentLevelPlugin />
        <TabIndentationPlugin />
        <LinkPlugin validateUrl={validateLinkUrl} />
        <MarkdownShortcutPlugin transformers={transformers} />
        {caretBottomOffset ? <KeepCaretAboveBottomOffsetPlugin bottomOffset={caretBottomOffset} /> : null}

        <SlashCommandPlugin
          snippets={enableSnippets ? snippetData?.email_snippets : undefined}
          disableHeadings={true}
          enableHtml={true}
          enableImages={enableImages}
          enableTable
        />
        {enableImages && <InlineImagePlugin />}
        <RawHtmlPlugin />

        {enablePlaceholders && <PlaceholderPlugin placeholders={placeholders} />}

        {plugins}

        {floatingAnchorElem && (
          <>
            <FloatingTextFormatToolbarPlugin anchorElem={floatingAnchorElem} />
            <FloatingLinkEditorPlugin anchorElem={floatingAnchorElem} />
            <FloatingLinkPreviewPlugin anchorElem={floatingAnchorElem} />
          </>
        )}
      </LexicalComposer>
    </div>
  )
}

const EmailEditor = forwardRef<EditorRefHandle, EmailEditorProps>(EmailEditorComponent)

EmailEditor.displayName = "EmailEditor"

export default EmailEditor
